<template>
	<div class="topbar-container">
		<b-navbar :class="topBarClass" :active.sync="burgerOpen">
			<template #brand>
				<b-navbar-item v-if="hasNoConnection" tag="div">
					<b-icon icon="wifi-off" class="mr-1"></b-icon>
					<p class="barelement">Keine Verbindung zum Server</p>
				</b-navbar-item>
				<b-navbar-item v-if="showUsername()" @click="routeHome()">
					<div style="height: 1.75rem; width: 1.75rem;">
						<img :src="getPicture()" style="border-radius: 9999px; object-fit: cover; height: 100%"
							alt="Profilbild">
					</div>
					<p class="barelement ml-1">
						{{ userDetails ? getUserName() : "" }}
					</p>
				</b-navbar-item>
				<session-countdown />
			</template>
			<template #start>
				<a v-if="backTarget" class="navbar-item icon-item" role="button"
					tabindex="0" aria-label="Zurück"
					@click="goBack()" @keyup.enter="goBack()">
					<b-icon icon="chevron-left" />
				</a>
				<b-navbar-item tag="div">
					<p class="barelement app-title">{{ title }}</p>
				</b-navbar-item>
				<!-- On phones the sidebar lives in the burger menu. -->
				<div class="mobile-sidebar">
					<side-bar />
				</div>
			</template>
			<template #end>
				<b-navbar-item v-if="section && section.add" tag="router-link"
					:to="{ name: section.add }" class="icon-item"
					aria-label="Neu anlegen">
					<b-icon icon="plus" />
				</b-navbar-item>
				<b-navbar-item class="logOutElement" v-if="showUsername()" tag="router-link" :to="{ path: '/log_out' }">
					<p class="barelement">
						Abmelden
					</p>
				</b-navbar-item>
			</template>
		</b-navbar>
		<div v-if="hasNoConnection" class="offline-banner">
			Der Server ist zurzeit nicht erreichbar. Angezeigte Daten können
			unvollständig sein — die Verbindung wird automatisch
			wiederhergestellt.
		</div>
		<version-modal v-model="isVersionModalActive" />
		<session-invalid-modal v-model="isSessionInvalidModalActive"></session-invalid-modal>
	</div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUserPicture } from "@/lib/Picture";
import { APP_LOGO } from "@/lib/config/AppConfigurations";
import { version } from "../../../package.json"
import VersionModal from "@/components/modals/VersionModal.vue";
import SessionInvalidModal from "@/components/modals/SessionInvalidModal.vue";
import SessionCountdown from "@/components/bars/SessionCountdown.vue";
import SideBar from "@/components/bars/Sidebar.vue";
import { sectionForRoute, detailForRoute } from "@/lib/config/Navigation";
import { reloadForNewVersion, clearVersionReloadMark } from "@/lib/versionReload";
import Parse from "parse/dist/parse.min.js";
import { isTestSystem } from "@/lib/isTestSystem";

export default {
	name: "TopBar",
	components: { VersionModal, SessionInvalidModal, SessionCountdown, SideBar },
	data() {
		return {
			app_logo: APP_LOGO,
			applicationVersion: version,
			isVersionModalActive: false,
			isSessionInvalidModalActive: false,
			hasNoConnection: false,
			statusIntervalId: undefined,
			burgerOpen: false
		};
	},
	computed: {
		...mapGetters({
			userDetails: "getCurrentUserMeta",
			user: "getCurrentUser",
			apiVersion: "versions/getApiVersion"
		}),
		section() {
			return sectionForRoute(this.$route.name);
		},
		detail() {
			return detailForRoute(this.$route.name);
		},
		title() {
			if (this.section) return this.section.label;
			if (this.detail) {
				return this.$store.state.detailTitle || this.detail.label;
			}
			return "Verwaltung";
		},
		backTarget() {
			if (this.section) return { path: "/" };
			if (this.detail && this.detail.list) {
				return { name: this.detail.list };
			}
			return undefined;
		},
		topBarClass() {
			if (this.hasNoConnection) { return "is-not-online"; }
			if (isTestSystem()) { return "is-testsystem"; }
			return "topbar";
		},
	},
	watch: {
		$route() {
			this.burgerOpen = false;
		},
		/*
			When the connection comes back, the LiveQuery subscriptions
			and initial queries of the open views have long failed --
			the data does not self-heal. Offer a reload instead of
			silently showing stale/empty tables.
		*/
		hasNoConnection(now, before) {
			if (before && !now) {
				this.$buefy.snackbar.open({
					message: "Verbindung zum Server wiederhergestellt.",
					type: "is-success",
					position: "is-top",
					actionText: "Neu laden",
					duration: 10000,
					onAction: () => { window.location.reload(); },
				});
			}
		},
	},
	async mounted() {
		this.checkServerStatus();
		document.addEventListener('visibilitychange', this.handleVisibilityChange);
		this.statusIntervalId = setInterval(this.checkServerStatus, 10000);
	},
	beforeDestroy() {
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		this.stopPolling();
	},
	methods: {
		stopPolling() {
			if (this.statusIntervalId === undefined) { return }
			clearInterval(this.statusIntervalId);
			this.statusIntervalId = undefined;
		},
		async checkServerStatus() {
			// A hidden tab has nothing to react to, and browsers throttle
			// its timers to once a minute anyway, which is exactly the
			// background drip that used to fill the error metrics.
			if (document.hidden) { return }
			let parseServerStatus = Parse.getServerHealth()
			let timeoutID = setTimeout(function () { this.hasNoConnection = true }.bind(this), 3000)
			parseServerStatus.then(() => { //value
				this.checkVersion()
				this.hasNoConnection = false
				clearTimeout(timeoutID);
			})
			parseServerStatus.catch(() => { //error
				this.hasNoConnection = true
				clearTimeout(timeoutID);
			})
		},
		async checkVersion() {
			Parse.Cloud.run("getDashboardVersion").then(async (apiVersion) => {
				this.$store.dispatch("versions/setApiVersion", apiVersion);
				this.isSessionInvalidModalActive = false;
				if (apiVersion != this.applicationVersion) {
					// A stale cached HTML is fixed by reloading; only
					// bother the user when that already failed once.
					this.isVersionModalActive = !(await reloadForNewVersion());
				}
				else {
					clearVersionReloadMark();
					this.isVersionModalActive = false;
				}
			}).catch((err) => {
				// An expired session cannot recover on its own, so every
				// further poll is a guaranteed failure. Stop asking.
				// Other errors (a network blip) must NOT close the modal;
				// only a successful call above may do that.
				if (err.code == Parse.Error.INVALID_SESSION_TOKEN) {
					this.stopPolling();
					this.isSessionInvalidModalActive = true;
				}
			});
		},
		handleVisibilityChange() {
			if (!document.hidden) {
				let parseServerStatus = Parse.getServerHealth()
				let timeoutID = setTimeout(function () { this.hasNoConnection = true }.bind(this), 3000)
				parseServerStatus.then(() => { //value
					this.hasNoConnection = false
					clearTimeout(timeoutID);
					
					if (this.apiVersion != this.applicationVersion) {
						this.isVersionModalActive = true
					} else {
						this.isVersionModalActive = false
					}
				})
				parseServerStatus.catch(() => { //error
					
					this.hasNoConnection = true
					clearTimeout(timeoutID);
				})
			}
		},
		async goBack() {
			if (this.backTarget) await this.$router.push(this.backTarget);
		},
		async routeHome() {
			if (this.$route.path !== "/") await this.$router.push({ path: "/" });

		},
		showUsername() {
			if (this.userDetails !== undefined) {
				return true
			}
			if (this.$route.path.includes("login") || this.$route.path.includes("log_out")) {
				return false
			}
			return false;
		},
		getPicture() {
			const pictureObject = {
				newProfilePicture: undefined,
				userPicture: this.userDetails.attributes.profile_picture ? this.userDetails.attributes.profile_picture._url : undefined,
				userGender: this.userDetails.attributes.gender
			}
			return getUserPicture(pictureObject)
		},
		getUserName() {
			return `${this.userDetails.attributes.first_name} ${this.userDetails.attributes.last_name}`
		},
	},
}
</script>

<style scoped>
.topbar-container {
	position: sticky;
	top: 0;
	z-index: 35;
}

::v-deep .img {
	object-fit: cover;
	width: 100%;
	height: 100%;
}

.barelement {
	color: white;
}

.app-title {
	font-weight: 600;
}

.icon-item ::v-deep .icon {
	color: white;
}

.icon-item:hover {
	background-color: rgba(255, 255, 255, 0.1);
}

.mobile-sidebar {
	display: none;
	text-align: left;
}

::v-deep .navbar-burger {
	color: white;
}

::v-deep .navbar-burger:hover {
	color: white;
	background-color: rgba(255, 255, 255, 0.1);
}

@media (min-width: 769px) and (max-width: 1023px) {
	::v-deep nav.navbar {
		display: flex;
		align-items: stretch;
	}

	::v-deep .navbar-burger {
		display: none;
	}

	::v-deep .navbar-menu {
		display: flex;
		flex-grow: 1;
		align-items: stretch;
		background: none;
		box-shadow: none;
		padding: 0;
	}

	::v-deep .navbar-start {
		display: flex;
		margin-right: auto;
	}

	::v-deep .navbar-end {
		display: flex;
		margin-left: auto;
	}

	/* Bulma's mobile items are blocks; center their content like the
	desktop layout does. */
	::v-deep .navbar-menu .navbar-item {
		display: flex;
		align-items: center;
	}
}

/* Real burger mode: white dropdown, dark text, sidebar inside. */
@media (max-width: 768px) {
	.mobile-sidebar {
		display: block;
	}

	::v-deep .navbar-menu .barelement {
		color: #363636;
	}

	.mobile-sidebar ::v-deep .sticky-menu {
		position: static;
		max-height: none;
	}
}

/* The brand fills the quarter above the sidebar; continue the
   sidebar's divider through the top bar, like the diary does. The
   sidebar column exists from 769px up, so the divider does too. */
@media (min-width: 769px) {
	::v-deep .navbar-brand {
		width: 25%;
		flex-shrink: 0;
		border-right: 2px solid rgba(255, 255, 255, 0.2);
	}
}

.logOutElement:hover {
	background-color:rgb(74, 74, 74);
}

.offline-banner {
	background-color: #f14668;
	color: white;
	text-align: center;
	padding: 0.4em 1em;
	font-size: 0.9rem;
}
</style>
